import React, { useEffect, useState } from "react";
import {
  CompanyDto,
  CompanyPaymentDto,
  ReconciliationDto,
  getCompanyPayments,
  addCompanyPayment,
  deleteCompanyPayment,
  getReconciliation,
  updateCompany,
} from "../../api/companies";

interface Props {
  company: CompanyDto;
  onUpdated: () => void;
}

type ViewMode = "payments" | "reconciliation";

const fmt = (n: number) =>
  new Intl.NumberFormat("ru-KZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

export function CompanySettlementsTab({ company, onUpdated }: Props) {
  const [view, setView] = useState<ViewMode>("payments");
  const [payments, setPayments] = useState<CompanyPaymentDto[]>([]);
  const [reconciliation, setReconciliation] = useState<ReconciliationDto | null>(null);
  const [loading, setLoading] = useState(false);

  // Начальный остаток
  const [openingBalance, setOpeningBalance] = useState(
    company.openingBalance !== undefined ? String(company.openingBalance) : "0"
  );
  const [openingDate, setOpeningDate] = useState(company.openingBalanceDate ?? "");
  const [balanceSaving, setBalanceSaving] = useState(false);

  // Форма нового платежа
  const [showForm, setShowForm] = useState(false);
  const [newDate, setNewDate] = useState(new Date().toISOString().slice(0, 10));
  const [newAmount, setNewAmount] = useState("");
  const [newType, setNewType] = useState<"in" | "out">("in");
  const [newDesc, setNewDesc] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPayments();
  }, [company.id]);

  useEffect(() => {
    setOpeningBalance(company.openingBalance !== undefined ? String(company.openingBalance) : "0");
    setOpeningDate(company.openingBalanceDate ?? "");
  }, [company.openingBalance, company.openingBalanceDate]);

  async function loadPayments() {
    setLoading(true);
    try {
      const p = await getCompanyPayments(company.id);
      setPayments(p);
    } finally {
      setLoading(false);
    }
  }

  async function loadReconciliation() {
    setLoading(true);
    try {
      const r = await getReconciliation(company.id);
      setReconciliation(r);
    } finally {
      setLoading(false);
    }
  }

  function switchView(v: ViewMode) {
    setView(v);
    if (v === "reconciliation") loadReconciliation();
  }

  async function saveOpeningBalance() {
    setBalanceSaving(true);
    try {
      await updateCompany(company.id, {
        openingBalance: parseFloat(openingBalance) || 0,
        openingBalanceDate: openingDate || undefined,
      });
      onUpdated();
    } finally {
      setBalanceSaving(false);
    }
  }

  async function handleAddPayment() {
    const amount = parseFloat(newAmount);
    if (!amount || isNaN(amount)) {
      alert("Введите сумму");
      return;
    }
    setSaving(true);
    try {
      await addCompanyPayment(company.id, {
        date: newDate,
        amount,
        type: newType,
        description: newDesc || undefined,
      });
      setNewAmount("");
      setNewDesc("");
      setShowForm(false);
      await loadPayments();
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(paymentId: number) {
    if (!window.confirm("Удалить платёж?")) return;
    await deleteCompanyPayment(company.id, paymentId);
    await loadPayments();
  }

  const totalIn = payments.filter(p => p.type === "in").reduce((s, p) => s + Number(p.amount), 0);
  const totalOut = payments.filter(p => p.type === "out").reduce((s, p) => s + Number(p.amount), 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Начальный остаток */}
      <div style={{ background: "#1e293b", borderRadius: 8, padding: "12px 16px" }}>
        <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 8 }}>Начальный остаток (входящий долг клиента)</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <label style={{ fontSize: 11, color: "#64748b", display: "block", marginBottom: 3 }}>Сумма ₸</label>
            <input
              style={inputStyle}
              type="number"
              value={openingBalance}
              onChange={e => setOpeningBalance(e.target.value)}
            />
          </div>
          <div>
            <label style={{ fontSize: 11, color: "#64748b", display: "block", marginBottom: 3 }}>На дату</label>
            <input
              style={inputStyle}
              type="date"
              value={openingDate}
              onChange={e => setOpeningDate(e.target.value)}
            />
          </div>
          <button
            style={btnSecondary}
            onClick={saveOpeningBalance}
            disabled={balanceSaving}
          >
            {balanceSaving ? "..." : "Сохранить"}
          </button>
        </div>
      </div>

      {/* Переключатель вкладок */}
      <div style={{ display: "flex", gap: 4 }}>
        <button
          style={view === "payments" ? tabActive : tabInactive}
          onClick={() => switchView("payments")}
        >
          Платежи
        </button>
        <button
          style={view === "reconciliation" ? tabActive : tabInactive}
          onClick={() => switchView("reconciliation")}
        >
          Акт сверки
        </button>
      </div>

      {/* ─── Платежи ─── */}
      {view === "payments" && (
        <div>
          {/* Итоги */}
          <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
            <div style={statCard}>
              <span style={{ color: "#94a3b8", fontSize: 11 }}>Получено</span>
              <span style={{ color: "#4ade80", fontWeight: 600 }}>+{fmt(totalIn)} ₸</span>
            </div>
            <div style={statCard}>
              <span style={{ color: "#94a3b8", fontSize: 11 }}>Выплачено</span>
              <span style={{ color: "#f87171", fontWeight: 600 }}>−{fmt(totalOut)} ₸</span>
            </div>
            <div style={statCard}>
              <span style={{ color: "#94a3b8", fontSize: 11 }}>Баланс платежей</span>
              <span style={{ color: totalIn - totalOut >= 0 ? "#4ade80" : "#f87171", fontWeight: 600 }}>
                {fmt(totalIn - totalOut)} ₸
              </span>
            </div>
          </div>

          {/* Кнопка добавить */}
          {!showForm && (
            <button style={btnPrimary} onClick={() => setShowForm(true)}>
              + Добавить платёж
            </button>
          )}

          {/* Форма */}
          {showForm && (
            <div style={{ background: "#1e293b", borderRadius: 8, padding: 12, marginBottom: 12, display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <div>
                  <label style={labelStyle}>Дата</label>
                  <input style={inputStyle} type="date" value={newDate} onChange={e => setNewDate(e.target.value)} />
                </div>
                <div>
                  <label style={labelStyle}>Сумма ₸</label>
                  <input style={inputStyle} type="number" placeholder="0" value={newAmount} onChange={e => setNewAmount(e.target.value)} />
                </div>
                <div>
                  <label style={labelStyle}>Тип</label>
                  <select style={{ ...inputStyle, padding: "6px 8px" }} value={newType} onChange={e => setNewType(e.target.value as "in" | "out")}>
                    <option value="in">Входящий (клиент заплатил)</option>
                    <option value="out">Исходящий (мы вернули)</option>
                  </select>
                </div>
              </div>
              <div>
                <label style={labelStyle}>Комментарий</label>
                <input style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="Назначение платежа..." />
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button style={btnPrimary} onClick={handleAddPayment} disabled={saving}>
                  {saving ? "Сохранение..." : "Добавить"}
                </button>
                <button style={btnSecondary} onClick={() => setShowForm(false)}>Отмена</button>
              </div>
            </div>
          )}

          {/* Список платежей */}
          {loading ? (
            <div style={{ color: "#64748b", fontSize: 13, padding: "12px 0" }}>Загрузка...</div>
          ) : payments.length === 0 ? (
            <div style={{ color: "#64748b", fontSize: 13, padding: "12px 0" }}>Платежей нет</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {payments.map(p => (
                <div key={p.id} style={{
                  display: "flex", alignItems: "center", gap: 10,
                  background: "#1e293b", borderRadius: 6, padding: "8px 12px",
                  flexWrap: "wrap",
                }}>
                  <span style={{ color: "#64748b", fontSize: 12, minWidth: 90 }}>{p.date}</span>
                  <span style={{
                    fontSize: 12, fontWeight: 600, minWidth: 110,
                    color: p.type === "in" ? "#4ade80" : "#f87171",
                  }}>
                    {p.type === "in" ? "+" : "−"}{fmt(Number(p.amount))} ₸
                  </span>
                  <span style={{ flex: 1, fontSize: 13, color: "#cbd5e1" }}>
                    {p.description || (p.type === "in" ? "Входящий платёж" : "Исходящий платёж")}
                  </span>
                  <button
                    style={{ background: "none", border: "none", color: "#475569", cursor: "pointer", fontSize: 16, padding: "0 4px" }}
                    onClick={() => handleDelete(p.id)}
                    title="Удалить"
                  >×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── Акт сверки ─── */}
      {view === "reconciliation" && (
        <div>
          {loading ? (
            <div style={{ color: "#64748b", fontSize: 13 }}>Загрузка...</div>
          ) : !reconciliation ? null : (
            <div>
              {reconciliation.openingBalanceDate && (
                <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 8 }}>
                  Начальный остаток на {reconciliation.openingBalanceDate}:{" "}
                  <strong style={{ color: "#e2e8f0" }}>{fmt(reconciliation.openingBalance)} ₸</strong>
                </div>
              )}

              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #334155" }}>
                      <th style={th}>Дата</th>
                      <th style={th}>Операция</th>
                      <th style={{ ...th, textAlign: "right" }}>Дебет ↑</th>
                      <th style={{ ...th, textAlign: "right" }}>Кредит ↓</th>
                      <th style={{ ...th, textAlign: "right" }}>Остаток</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reconciliation.rows.length === 0 && (
                      <tr>
                        <td colSpan={5} style={{ padding: "12px 8px", color: "#64748b", textAlign: "center" }}>
                          Нет операций
                        </td>
                      </tr>
                    )}
                    {reconciliation.rows.map(row => (
                      <tr key={row.id} style={{ borderBottom: "1px solid #1e293b" }}>
                        <td style={td}>{row.date}</td>
                        <td style={{ ...td, color: row.dealId ? "#a78bfa" : "#cbd5e1" }}>
                          {row.description}
                        </td>
                        <td style={{ ...td, textAlign: "right", color: "#f87171" }}>
                          {row.debit > 0 ? fmt(row.debit) : ""}
                        </td>
                        <td style={{ ...td, textAlign: "right", color: "#4ade80" }}>
                          {row.credit > 0 ? fmt(row.credit) : ""}
                        </td>
                        <td style={{
                          ...td, textAlign: "right", fontWeight: 600,
                          color: row.balance > 0 ? "#fbbf24" : row.balance < 0 ? "#4ade80" : "#94a3b8",
                        }}>
                          {fmt(row.balance)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: "2px solid #334155" }}>
                      <td colSpan={4} style={{ ...td, fontWeight: 600, color: "#94a3b8" }}>
                        Итоговое сальдо
                      </td>
                      <td style={{
                        ...td, textAlign: "right", fontWeight: 700, fontSize: 13,
                        color: reconciliation.finalBalance > 0 ? "#fbbf24" : reconciliation.finalBalance < 0 ? "#4ade80" : "#94a3b8",
                      }}>
                        {fmt(reconciliation.finalBalance)} ₸
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div style={{ marginTop: 8, fontSize: 11, color: "#475569" }}>
                🟡 Положительное сальдо — клиент должен нам. 🟢 Отрицательное — мы должны клиенту.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Стили ───────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  background: "#0f172a",
  border: "1px solid #334155",
  borderRadius: 6,
  padding: "6px 10px",
  color: "#e2e8f0",
  fontSize: 13,
  outline: "none",
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  color: "#64748b",
  display: "block",
  marginBottom: 3,
};

const btnPrimary: React.CSSProperties = {
  background: "#3b82f6",
  border: "none",
  borderRadius: 6,
  padding: "7px 14px",
  color: "#fff",
  fontSize: 13,
  cursor: "pointer",
  fontWeight: 500,
};

const btnSecondary: React.CSSProperties = {
  background: "none",
  border: "1px solid #334155",
  borderRadius: 6,
  padding: "7px 14px",
  color: "#94a3b8",
  fontSize: 13,
  cursor: "pointer",
};

const tabActive: React.CSSProperties = {
  background: "#3b82f6",
  border: "none",
  borderRadius: 6,
  padding: "6px 14px",
  color: "#fff",
  fontSize: 13,
  cursor: "pointer",
};

const tabInactive: React.CSSProperties = {
  background: "none",
  border: "1px solid #334155",
  borderRadius: 6,
  padding: "6px 14px",
  color: "#64748b",
  fontSize: 13,
  cursor: "pointer",
};

const statCard: React.CSSProperties = {
  background: "#1e293b",
  borderRadius: 6,
  padding: "8px 12px",
  display: "flex",
  flexDirection: "column",
  gap: 2,
  minWidth: 120,
};

const th: React.CSSProperties = {
  padding: "6px 8px",
  textAlign: "left",
  color: "#64748b",
  fontWeight: 500,
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

const td: React.CSSProperties = {
  padding: "7px 8px",
  color: "#cbd5e1",
};
